import {
  type ReactNode,
  createContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createConsumer, type ActionCableConsumer } from '@rails/actioncable';
import { wsURL } from '../config/endpoints';
import { useLogin } from '../hooks/useLogin';
import type { UpdateMessage } from '../hooks/useUpdates';
import wsTokenApi from '../api/fetch/wsTokenApi';

type Unsubscribe = () => void;

const RECONNECT_MIN_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const CONNECT_TIMEOUT_MS = 15_000;

export interface CableContextValue {
  cable: ActionCableConsumer | null;
  onMessage: (handler: (msg: UpdateMessage) => void) => Unsubscribe;
  onReconnect: (handler: () => void) => Unsubscribe;
}

const noop = (): Unsubscribe => () => undefined;

export const CableContext = createContext<CableContextValue>({
  cable: null,
  onMessage: noop,
  onReconnect: noop,
});

interface CableProviderProps {
  children: ReactNode;
}

export function CableProvider({ children }: CableProviderProps) {
  const { userData, isLoggedIn } = useLogin();
  const token = isLoggedIn ? userData?.token : undefined;

  const messageHandlersRef = useRef<Set<(msg: UpdateMessage) => void>>(new Set());
  const reconnectHandlersRef = useRef<Set<() => void>>(new Set());
  const consumerRef = useRef<ActionCableConsumer | null>(null);

  // The handshake authenticates with a ~45s single-purpose token fetched per
  // connection attempt — the session JWT must never ride the /ws URL (#152).
  // actioncable's own monitor reconnects with the consumer's fixed URL, which
  // would replay an expired token; so on every drop we tear the consumer down
  // and rebuild it with a fresh token, with exponential backoff. A connect
  // timeout covers handshake rejection, where no mixin callback ever fires.
  // A consumer torn down while the library's own reopen() is pending still
  // opens once more with its old token; the server rejects it with
  // reconnect: false and the library stops there (cable.reconnect.test.tsx).
  useEffect(() => {
    if (!token) {
      consumerRef.current = null;
      return undefined;
    }

    let disposed = false;
    let consumer: ActionCableConsumer | null = null;
    let subscription: { unsubscribe: () => void } | null = null;
    let retryDelay = RECONNECT_MIN_DELAY_MS;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let connectTimeout: ReturnType<typeof setTimeout> | undefined;

    const teardown = () => {
      if (connectTimeout !== undefined) clearTimeout(connectTimeout);
      connectTimeout = undefined;
      subscription?.unsubscribe();
      subscription = null;
      consumer?.disconnect();
      consumer = null;
      consumerRef.current = null;
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      teardown();
      retryTimer = setTimeout(() => void connect(), retryDelay);
      retryDelay = Math.min(retryDelay * 2, RECONNECT_MAX_DELAY_MS);
    };

    const connect = async () => {
      if (disposed) return;
      let wsToken: string;
      try {
        wsToken = (await wsTokenApi.create()).ws_token;
      } catch {
        scheduleReconnect();
        return;
      }
      if (disposed) return;

      consumer = createConsumer(`${wsURL}?token=${wsToken}`);
      consumerRef.current = consumer;
      connectTimeout = setTimeout(scheduleReconnect, CONNECT_TIMEOUT_MS);
      subscription = consumer.subscriptions.create(
        { channel: 'UpdatesChannel' },
        {
          received: (data: UpdateMessage) => {
            messageHandlersRef.current.forEach((h) => h(data));
          },
          connected: () => {
            if (connectTimeout !== undefined) clearTimeout(connectTimeout);
            connectTimeout = undefined;
            retryDelay = RECONNECT_MIN_DELAY_MS;
            reconnectHandlersRef.current.forEach((h) => h());
          },
          disconnected: () => scheduleReconnect(),
        },
      );
    };

    void connect();

    return () => {
      disposed = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      teardown();
    };
  }, [token]);

  const value = useMemo<CableContextValue>(
    () => ({
      get cable() {
        return consumerRef.current;
      },
      onMessage: (handler) => {
        messageHandlersRef.current.add(handler);
        return () => messageHandlersRef.current.delete(handler);
      },
      onReconnect: (handler) => {
        reconnectHandlersRef.current.add(handler);
        return () => reconnectHandlersRef.current.delete(handler);
      },
    }),
    [],
  );

  return <CableContext.Provider value={value}>{children}</CableContext.Provider>;
}
